/**
 * Einstellungen — der EINZIGE Ort für alles, was der Spieler einstellt und was
 * einen Neustart überleben soll.
 *
 * Warum eigenes Modul: Sonst wandert `localStorage.getItem(...)` nach und nach
 * in jede Datei, und niemand weiß mehr, welche Schlüssel es gibt
 * (docs/2026-07-26-ueberarbeitung.md, A5).
 */

import type { Automatikgrad } from '@pg/protocol';

export type Zoomstufe = 'nah' | 'mittel' | 'weit';

export interface Einstellungen {
  zoom: Zoomstufe;
  automatikgrad: Automatikgrad;
  /** Fähigkeiten, die der Spieler auf Handbetrieb gestellt hat. */
  handbetrieb: string[];
  /** Gangart: 'flink' oder 'gemessen'. */
  tempoprofil: string;
}

const SCHLUESSEL = 'pizza-gold.einstellungen';

const STANDARD: Einstellungen = {
  zoom: 'mittel',
  automatikgrad: 'grundangriff',
  handbetrieb: [],
  tempoprofil: 'gemessen',
};

export const ZOOMSTUFEN: readonly Zoomstufe[] = ['nah', 'mittel', 'weit'];
export const AUTOMATIKGRADE: readonly Automatikgrad[] = ['aus', 'grundangriff', 'freigegeben'];
export const TEMPOPROFILE = ['flink', 'gemessen'] as const;

export const AUTOMATIK_TEXT: Record<Automatikgrad, { kurz: string; lang: string }> = {
  aus: { kurz: 'Aus', lang: 'Nichts feuert von selbst. Alles Handarbeit.' },
  grundangriff: {
    kurz: 'Grund',
    lang: 'Nur dein Grundangriff schlägt von allein zu. Er kostet nichts und hält dich nie an.',
  },
  freigegeben: {
    kurz: 'Frei',
    lang: 'Grundangriff plus die Fähigkeiten, die du selbst freigegeben hast. Sprünge nie.',
  },
};

export const TEMPO_TEXT: Record<string, { kurz: string; lang: string }> = {
  flink: { kurz: 'Flink', lang: 'Zügig unterwegs, schnelle Schlagfolge.' },
  gemessen: {
    kurz: 'Gemessen',
    lang: 'Schrittgeschwindigkeit, schwere Schläge — etwa einer alle zwei Sekunden.',
  },
};

let zustand: Einstellungen = lade();

function lade(): Einstellungen {
  try {
    const roh = localStorage.getItem(SCHLUESSEL);
    if (!roh) return { ...STANDARD };
    const gelesen = JSON.parse(roh) as Partial<Einstellungen>;
    return {
      zoom: ZOOMSTUFEN.includes(gelesen.zoom as Zoomstufe) ? (gelesen.zoom as Zoomstufe) : STANDARD.zoom,
      automatikgrad: AUTOMATIKGRADE.includes(gelesen.automatikgrad as Automatikgrad)
        ? (gelesen.automatikgrad as Automatikgrad)
        : STANDARD.automatikgrad,
      handbetrieb: Array.isArray(gelesen.handbetrieb) ? gelesen.handbetrieb : [],
      tempoprofil: (TEMPOPROFILE as readonly string[]).includes(gelesen.tempoprofil ?? '')
        ? (gelesen.tempoprofil as string)
        : STANDARD.tempoprofil,
    };
  } catch {
    // Kaputter oder gesperrter Speicher darf das Spiel nicht aufhalten.
    return { ...STANDARD };
  }
}

function sichern(): void {
  try {
    localStorage.setItem(SCHLUESSEL, JSON.stringify(zustand));
  } catch {
    /* Privater Modus o. Ä. — das Spiel läuft trotzdem. */
  }
}

export function einstellungen(): Readonly<Einstellungen> {
  return zustand;
}

export function setzeZoom(z: Zoomstufe): void {
  zustand.zoom = z;
  sichern();
}

export function naechsterZoom(): Zoomstufe {
  const i = ZOOMSTUFEN.indexOf(zustand.zoom);
  return ZOOMSTUFEN[(i + 1) % ZOOMSTUFEN.length] as Zoomstufe;
}

export function setzeAutomatikgrad(g: Automatikgrad): void {
  zustand.automatikgrad = g;
  sichern();
}

export function naechsterAutomatikgrad(): Automatikgrad {
  const i = AUTOMATIKGRADE.indexOf(zustand.automatikgrad);
  return AUTOMATIKGRADE[(i + 1) % AUTOMATIKGRADE.length] as Automatikgrad;
}

export function setzeHandbetrieb(faehigkeit: string, an: boolean): void {
  const ohne = zustand.handbetrieb.filter((f) => f !== faehigkeit);
  zustand.handbetrieb = an ? [...ohne, faehigkeit] : ohne;
  sichern();
}

export function istHandbetrieb(faehigkeit: string): boolean {
  return zustand.handbetrieb.includes(faehigkeit);
}

export function setzeTempoprofil(p: string): void {
  zustand.tempoprofil = p;
  sichern();
}

export function naechstesTempoprofil(): string {
  const i = TEMPOPROFILE.indexOf(zustand.tempoprofil as (typeof TEMPOPROFILE)[number]);
  return TEMPOPROFILE[(i + 1) % TEMPOPROFILE.length]!;
}
